import React from 'react';
import { Steps } from 'antd';
import moment from 'moment';
import {
  ant,
  Button,
  Container,
  Dropdown,
  DropdownMenu,
  Flex,
  FlexItem,
  H4,
  Icon,
  styled,
  Tag,
  Text,
  Tooltip,
} from '@erecruit/ui-kit';
import { NamedObject, Resources } from '@erecruit/types';
import { Intl } from '@erecruit/core';
import { usePermissions } from '@erecruit/modules';
import { StepProps } from '@erecruit/ui-kit/src/molecules/Steps/Step';

import { DATE_FORMAT } from '../../../constants';
import { MatchAction } from '../types';

const { FormattedMessage } = Intl;

export interface MatchTimelineProps {
  isUpdating: boolean;
  actions: MatchAction[];
  match: Resources.Match;
  matchStatus: NamedObject<number>;
  onMatchAction(matchActionType: Resources.MatchActionType): void;
  steps: Resources.MatchTransitionStep[];
}

/**
 * Match status is different than Match timeline (step) status.
 * In order to show Front-end specific status for the FullPlacement
 * actual Match status should be used. This function resolves the issue.
 *
 * @param matchStatus
 * @param matchStepStatus
 */
const resolveStatus = (
  matchStatus: NamedObject<number>,
  matchStepStatus: NamedObject<Resources.MatchStatusType>,
) =>
  matchStepStatus.id === Resources.MatchStatusType.FullPlacement
    ? matchStatus
    : matchStepStatus;

const OuterTag = styled(Tag)({
  margin: '-10px 0px',
  padding: 10,
});

const TimelineActions = styled(Flex)({
  position: 'absolute',
  top: 210,
  width: '100%',
});

const ButtonWithoutPadding = styled(Button)({ padding: 0 });

const FlexWithButtonPadding = styled(Flex)({ padding: '6px 12px' });

const TimelineButtons = ({
  actions,
  isUpdating,
  onMatchAction,
}: Required<
  Pick<MatchTimelineProps, 'actions' | 'isUpdating' | 'onMatchAction'>
>) => {
  if (actions.length === 3) {
    return (
      <>
        <Button
          type="primary"
          disabled={isUpdating}
          onClick={() => onMatchAction(Resources.MatchActionType.MakeOffer)}
        >
          <TimelineButtonText action={Resources.MatchActionType.MakeOffer} />
        </Button>
        <ButtonWithoutPadding>
          <Dropdown
            overlay={() => (
              <DropdownMenu
                items={[
                  {
                    text: (
                      <Flex padding={[2, 6]} align="center" divider="top">
                        <TimelineButtonText
                          action={Resources.MatchActionType.InviteForInterview}
                        />
                      </Flex>
                    ),
                    onClick: () =>
                      onMatchAction(
                        Resources.MatchActionType.InviteForInterview,
                      ),
                  },
                  {
                    text: (
                      <Flex padding={[2, 6]} align="center">
                        <TimelineButtonText
                          action={Resources.MatchActionType.Reject}
                        />
                      </Flex>
                    ),
                    onClick: () =>
                      onMatchAction(Resources.MatchActionType.Reject),
                  },
                ]}
              />
            )}
          >
            <FlexWithButtonPadding align="center">
              <H4>
                <FormattedMessage
                  id={messageIds.vms.matchRecord.timeline.more}
                />
              </H4>
              <Icon type="arrow-dropdown-down" />
            </FlexWithButtonPadding>
          </Dropdown>
        </ButtonWithoutPadding>
      </>
    );
  }

  return (
    <>
      {actions.map(action => (
        <FlexItem key={action.type} flex="none">
          <Button
            type={
              action.type === Resources.MatchActionType.MakeOffer
                ? 'primary'
                : 'secondary'
            }
            disabled={isUpdating}
            onClick={() => onMatchAction(action.type)}
          >
            <TimelineButtonText action={action.type} />
          </Button>
        </FlexItem>
      ))}
    </>
  );
};

const TimelineButtonsContainer = styled(Flex)(
  ({ theme: { shadows, newColors } }) => ({
    background: newColors.bgPageSecondary,
    padding: 24,
    boxShadow: shadows.widgetCard,
    borderRadius: 8,
  }),
);

const TimelineButtonText = ({
  action,
}: {
  action: Resources.MatchActionType;
}) => {
  const isMakeOffer = action === Resources.MatchActionType.MakeOffer;

  if (isMakeOffer) {
    return (
      <H4 color="headerTertiary">
        <FormattedMessage
          id={Intl.strToLocalizationKeys(
            `vms.matchRecord.timeline.${Resources.MatchActionType[action]}`,
          )}
        />
      </H4>
    );
  }

  return (
    <H4>
      <FormattedMessage
        id={Intl.strToLocalizationKeys(
          `vms.matchRecord.timeline.${Resources.MatchActionType[action]}`,
        )}
      />
    </H4>
  );
};

const TimelineContainer = styled(Container)({
  background: 'transparent',
  margin: 0,
  height: 209,
  overflow: 'visible',
});

const TimelineStep = styled(({ current, title, ...otherProps }) => {
  const stepTitle = current ? (
    <H4 align="center" padding={[5, 0, 0, 5]}>
      {title}
    </H4>
  ) : (
    <Text align="center" padding={[5, 0]}>
      {title}
    </Text>
  );
  return <Steps.Step title={stepTitle} {...otherProps} />;
})<StepProps & { current?: boolean }>(({ theme: { newColors } }) =>
  ant({
    '&.steps-item-finish .steps-item-tail, &.steps-item-process-tail': {
      '::after': {
        backgroundColor: newColors.statusSuccess,
      },
    },
    '.steps-item-tail': {
      '::after': {
        height: 2,
      },
    },
    '.steps-label-vertical, .steps-item-tail': {
      padding: '4.5px 14px',
    },
  }),
);

const TimelineStepIcon = ({
  matchStatusType,
  isLastItem,
}: {
  matchStatusType: Resources.MatchStatusType;
  isLastItem: boolean;
}) => {
  if (matchStatusType === Resources.MatchStatusType.Rejected) {
    return (
      <OuterTag circle color="critical">
        <Tag circle color="bgPagePrimary">
          <Icon type="close" color="critical" size="large" />
        </Tag>
      </OuterTag>
    );
  }

  if (
    (matchStatusType === Resources.MatchStatusType.InterviewRequested ||
      matchStatusType === Resources.MatchStatusType.OfferWillBeMade) &&
    isLastItem
  ) {
    return (
      <OuterTag circle color="pending">
        <Tag circle color="bgPagePrimary">
          <Icon type="pending" color="pending" size="large" />
        </Tag>
      </OuterTag>
    );
  }

  if (
    matchStatusType !== Resources.MatchStatusType.FullPlacement &&
    isLastItem
  ) {
    return (
      <OuterTag circle color="success">
        <Tag circle color="bgPagePrimary">
          <Icon type="pending" color="success" size="large" />
        </Tag>
      </OuterTag>
    );
  }

  return (
    <Tag circle color="success">
      <Icon type="check" color="bodyTertiary" size="large" />
    </Tag>
  );
};

const TimelineSteps = styled(Steps)<{ hasActions?: boolean }>(
  ({ hasActions, theme: { spaceSize } }) => ({
    padding: `${12 * spaceSize}px ${hasActions ? '20%' : '10%'}`,
    display: 'flex',
    justifyContent: 'center',
  }),
);

interface TimelineStepTooltipProps {
  changedBy: NamedObject<string>;
  changedOn: Date;
  match: Resources.Match;
  matchStatus: NamedObject<Resources.MatchStatusType>;
  statusDescription?: string;
}

const OverlayContainer = styled.div({
  '&': {
    padding: 10,
    '& > div': { paddingTop: 12 },
  },
});

const TimelineStepTooltip = ({
  changedBy,
  changedOn,
  match,
  matchStatus,
  statusDescription,
}: TimelineStepTooltipProps) => {
  return (
    <OverlayContainer>
      <H4>{matchStatus.name}</H4>
      <div>
        <Text>
          <FormattedMessage id={messageIds.vms.matchRecord.timeline.date} />{' '}
          {moment(changedOn).format(DATE_FORMAT)}
        </Text>
        <Text>
          <FormattedMessage
            id={messageIds.vms.matchRecord.timeline.changedBy}
          />{' '}
          {changedBy.name}
        </Text>
      </div>
      {statusDescription && (
        <div>
          <Text>{statusDescription}</Text>
        </div>
      )}
      {matchStatus.id === Resources.MatchStatusType.Rejected &&
      match.rejectionReason ? (
        <div>
          <Text>
            <FormattedMessage
              id={messageIds.vms.matchRecord.timeline.rejectionReason}
            />{' '}
            {match.rejectionReason.name}
          </Text>
          <Text>{match.rejectionComment}</Text>
        </div>
      ) : null}
      {matchStatus.id === Resources.MatchStatusType.RejectionRequested &&
      match.rejectionRequestedReason ? (
        <div>
          <Text>Rejection reason: {match.rejectionRequestedReason.name}</Text>
          <Text>{match.rejectionRequestedNote}</Text>
        </div>
      ) : null}
      {match.interviewRequestedNote ? (
        <div>
          <Text>{match.interviewRequestedNote}</Text>
        </div>
      ) : null}
      {match.makeOfferRequestedNote ? (
        <div>
          <Text>{match.makeOfferRequestedNote}</Text>
        </div>
      ) : null}
    </OverlayContainer>
  );
};

export default ({
  actions,
  isUpdating,
  match,
  matchStatus,
  onMatchAction,
  steps,
}: MatchTimelineProps) => {
  const { filterByPermissions } = usePermissions();
  const allowedActions = filterByPermissions(actions);

  return (
    <TimelineContainer gutter={10}>
      <TimelineSteps
        current={steps.length - 1}
        labelPlacement="vertical"
        hasActions={!!allowedActions.length}
      >
        {steps.map(
          (
            {
              changedBy,
              changedOn,
              matchStatus: stepMatchStatus,
              statusDescription,
            },
            index,
          ) => {
            const isCurrent = index === steps.length - 1;
            const status = resolveStatus(matchStatus, stepMatchStatus);
            return (
              <TimelineStep
                key={index}
                current={isCurrent}
                title={status?.name}
                icon={
                  <Tooltip
                    overlay={
                      <TimelineStepTooltip
                        changedBy={changedBy}
                        changedOn={changedOn}
                        match={match}
                        matchStatus={stepMatchStatus}
                        statusDescription={statusDescription}
                      />
                    }
                  >
                    <div>
                      <TimelineStepIcon
                        matchStatusType={stepMatchStatus.id}
                        isLastItem={index === steps.length - 1}
                      />
                    </div>
                  </Tooltip>
                }
              />
            );
          },
        )}
      </TimelineSteps>
      {allowedActions.length ? (
        <TimelineActions align="center" justify="space-between">
          <FlexItem />
          <FlexItem flex={0}>
            <TimelineButtonsContainer align="left" gutter={2}>
              <TimelineButtons
                actions={allowedActions}
                isUpdating={isUpdating}
                onMatchAction={onMatchAction}
              />
            </TimelineButtonsContainer>
          </FlexItem>
          <FlexItem />
        </TimelineActions>
      ) : null}
    </TimelineContainer>
  );
};
